'use client';

import React, { createContext, useContext, useMemo } from 'react';

export interface OrderReferenceContextValue {
  orderReference?: string;
}

const OrderReferenceContext = createContext<OrderReferenceContextValue | undefined>(undefined);

/**
 * Returns `undefined` outside the provider on purpose — the same CMS tiles render on pages that
 * have no order in scope (my-account, landing pages), where an order reference simply does not
 * exist. Consumers must treat `undefined` as "no order", not as a misconfiguration.
 */
export function useOrderReference(): OrderReferenceContextValue | undefined {
  return useContext(OrderReferenceContext);
}

interface OrderReferenceProviderProps {
  orderReference?: string;
  children: React.ReactNode;
}

export function OrderReferenceProvider({
  orderReference,
  children,
}: Readonly<OrderReferenceProviderProps>) {
  const contextValue = useMemo(() => ({ orderReference }), [orderReference]);

  return (
    <OrderReferenceContext.Provider value={contextValue}>{children}</OrderReferenceContext.Provider>
  );
}
